import React, { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { dialogs, i18next, assets, view, serviceClient, uuid } from '@yola/ws-sdk';
import { ProgressDialog } from '@yola/ws-ui';
import withRetry from 'src/js/modules/utils/with-retry';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import stockGallery from 'src/js/modules/stock-gallery';
import focalPointConstants from 'src/js/modules/focal-point/constants/common';
import getGroupAspectRatio from '../helpers/get-group-aspect-ratio';
import getMediaGroupId from '../helpers/get-media-group-id';
import generateItemId from '../helpers/generate-item-id';
import getImageData from '../helpers/get-image-data';
import mediaTypes from '../constants/media-types';

const {
  constants: { stockIds },
  helpers: { generateIstockImageSrc },
} = stockGallery;

const { PHOTO_LOAD_ERROR, GALLERY_DIALOG } = dialogTypes;
const { DEFAULT_CONTENT_POSITION_STRING_VALUE } = focalPointConstants;

const defaultCaptions = (imageForLoadTotal, imageCount) => ({
  title: i18next.t('{progress}/{total} images uploaded', {
    total: imageForLoadTotal,
    progress: imageCount,
  }),
  description: i18next.t('Your uploads will be complete momentarily'),
  cancel: i18next.t('Cancel'),
});

const defaultItems = [];
const defaultInitialItems = [];
const defaultSelectedPhotos = [];

function PhotoStockSavingDialog({
  items = defaultItems,
  initialItems = defaultInitialItems,
  elementId,
  selectedPhotos = defaultSelectedPhotos,
  stockId,
  onDialogCancel,
}) {
  const imageForLoadTotal = selectedPhotos.length;
  const [count, setCounter] = useState(0);
  let counter = 0;
  const abortRequestController = useMemo(() => new AbortController(), []);

  const progress = (i) => {
    setCounter(i);
  };

  const handleCancel = useCallback(() => {
    abortRequestController.abort();
    onDialogCancel();
    dialogs.operations.hide();
  }, [abortRequestController, onDialogCancel]);

  const onDismiss = useCallback(() => {
    onDialogCancel({ dialogType: PHOTO_LOAD_ERROR });

    dialogs.operations.show(GALLERY_DIALOG, {
      items,
      elementId,
    });
  }, [elementId, items, onDialogCancel]);

  const handleOperation = useCallback(() => {
    const client = serviceClient.get();
    const unsplashCreateAssetsFromPhotoWithRetry = withRetry(
      client.unsplashCreateAssetsFromPhoto.bind(client)
    );
    const getImageDataWithRetry = withRetry(getImageData);
    const aspectRatio = getGroupAspectRatio(items) || getGroupAspectRatio(initialItems) || 1;
    const mediaGroupId = getMediaGroupId(items) || getMediaGroupId(initialItems) || uuid();

    const operation = selectedPhotos.map(async (item) => {
      let assetSource;

      if (stockId === stockIds.ISTOCK) {
        assetSource = generateIstockImageSrc(item.id);
      } else {
        const { data } = await unsplashCreateAssetsFromPhotoWithRetry({
          photoId: item.id,
          imagePlacement: assets.constants.IMAGE_PLACEMENTS.INLINE,
        });
        assetSource = data.source;
      }

      // eslint-disable-next-line no-plusplus
      progress(++counter);

      const liveDoc = view.accessors.getLiveDocument();
      const { width, height } = await getImageDataWithRetry(assetSource, liveDoc.defaultView);

      return {
        id: generateItemId(counter, Date.now()),
        originWidth: width,
        originHeight: height,
        aspectRatio,
        originSrc: assetSource,
        src: assetSource,
        alt: '',
        title: '',
        description: '',
        contentPosition: DEFAULT_CONTENT_POSITION_STRING_VALUE,
        contentScale: 1,
        mediaGroupId,
        mediaType: mediaTypes.IMAGE,
        isSaved: true,
      };
    });

    Promise.all(operation)
      .then((value) => {
        const operationResult = value.filter((item) => item);
        let result = items;

        if (operationResult.length) {
          result = [...items, ...value];
        }

        return result;
      })
      .then((result) => {
        dialogs.operations.show(GALLERY_DIALOG, {
          items: result,
          elementId,
        });
      })
      .catch((error) => {
        if (abortRequestController?.signal?.aborted) {
          dialogs.operations.show(GALLERY_DIALOG, {
            items: initialItems.length ? initialItems : items,
            elementId,
          });
        } else {
          dialogs.operations.show(PHOTO_LOAD_ERROR, {
            onDismiss,
            isApiRateExceeded: Boolean(error && error.status === 429),
          });
        }
      });
  }, [
    selectedPhotos,
    stockId,
    counter,
    elementId,
    abortRequestController,
    items,
    initialItems,
    onDismiss,
  ]);

  useEffect(() => {
    handleOperation();
  }, [handleOperation]);

  return (
    <ProgressDialog
      captions={defaultCaptions(imageForLoadTotal, count)}
      value={(count / imageForLoadTotal) * 100}
      cancelable
      overlay="visible"
      containerClassName="ws-loading-progress-dialog ws-dialog-fade"
      onCancel={handleCancel}
    />
  );
}

PhotoStockSavingDialog.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})),
  initialItems: PropTypes.arrayOf(PropTypes.shape({})),
  selectedPhotos: PropTypes.arrayOf(PropTypes.shape({})),
  stockId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  // Passed from ws-editor's `dialogs-container`:
  onDialogCancel: PropTypes.func.isRequired,
};

export default PhotoStockSavingDialog;
